import React, { type ReactNode } from "react";
import { AlertTriangle, Home, RefreshCw, ChevronDown } from "lucide-react";

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  isExpanded: boolean;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isExpanded: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      errorInfo,
    });
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isExpanded: false,
    });
  };

  toggleExpanded = () => {
    this.setState((prev) => ({ isExpanded: !prev.isExpanded }));
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback && this.state.error) {
        return this.props.fallback(this.state.error, this.resetError);
      }

      const isDev = process.env.NODE_ENV === "development";

      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white p-4">
          <div className="bg-dark-2 border border-dark-1 rounded-2xl p-8 max-w-2xl w-full shadow-2xl">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle size={32} className="text-red-500" />
            </div>
            <h1 className="text-3xl font-bold font-redhat-display mb-2 text-white text-center">
              Oops! Something went wrong
            </h1>

            <p className="text-white/60 text-center text-sm mb-8 font-inter leading-relaxed">
              We encountered an unexpected error. Please try again or return to
              the home page.
            </p>
            {isDev && this.state.error && (
              <div className="mb-6">
                <button
                  onClick={this.toggleExpanded}
                  className="w-full flex items-center justify-between p-3 bg-dark-3 hover:bg-dark-1 rounded-lg transition-colors text-left mb-2 border border-dark-1"
                >
                  <span className="text-red-400 text-sm font-mono">
                    Error Details (Dev)
                  </span>
                  <ChevronDown
                    size={18}
                    className={`transition-transform ${
                      this.state.isExpanded ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {this.state.isExpanded && (
                  <div className="bg-dark-3 border border-dark-1 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-red-500 text-xs font-bold mb-1">
                        Error Message:
                      </p>
                      <p className="text-white/70 text-xs font-mono wrap-break-word">
                        {this.state.error.message}
                      </p>
                    </div>

                    {this.state.errorInfo?.componentStack && (
                      <div>
                        <p className="text-red-400 text-xs font-bold mb-1">
                          Component Stack:
                        </p>
                        <pre className="text-white/60 text-xs font-mono overflow-auto max-h-48 bg-black/50 p-2 rounded border border-dark-1">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}

                    {this.state.error?.stack && (
                      <div>
                        <p className="text-red-400 text-xs font-bold mb-1">
                          Stack Trace:
                        </p>
                        <pre className="text-white/60 text-xs font-mono overflow-auto max-h-48 bg-black/50 p-2 rounded border border-dark-1">
                          {this.state.error.stack}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.resetError}
                className="flex items-center gap-2 px-6 py-3 bg-dark-1 hover:bg-dark-3 rounded-xl transition-colors text-sm font-bold border border-dark-1 hover:border-dark-2"
              >
                <RefreshCw size={16} />
                Try Again
              </button>

              <button
                onClick={() => (window.location.href = "/")}
                className="flex items-center gap-2 px-6 py-3 bg-primary-1 hover:bg-blue-600 text-white rounded-xl transition-colors text-sm font-bold"
              >
                <Home size={16} />
                Back to Home
              </button>
            </div>
          </div>
          {isDev && (
            <p className="text-white/40 text-xs mt-8 text-center max-w-2xl">
              Tip: This error boundary caught a runtime error. Check the browser
              console for more details. In production, users will see a
              simplified error message.
            </p>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
